import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Flame,
  Droplets,
  Sun,
  Shield,
  UserX,
  Plane,
  Heart,
  Eye
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { EmergencyService, HazardPlan, EmergencyProfile } from '../../lib/emergencyService';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { SearchInput } from '../common/SearchInput';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';

const hazardTypes = {
  medical_emergency: { label: 'Medical Emergency', icon: <Heart size={20} />, color: 'bg-red-100 text-red-800 border-red-200' },
  fire_bushfire: { label: 'Fire/Bushfire', icon: <Flame size={20} />, color: 'bg-orange-100 text-orange-800 border-orange-200' },
  flood_storm: { label: 'Flood/Storm', icon: <Droplets size={20} />, color: 'bg-blue-100 text-blue-800 border-blue-200' },
  heatwave: { label: 'Heatwave', icon: <Sun size={20} />, color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  cyber_compromise: { label: 'Cyber Compromise', icon: <Shield size={20} />, color: 'bg-purple-100 text-purple-800 border-purple-200' },
  missing_person: { label: 'Missing Person', icon: <UserX size={20} />, color: 'bg-pink-100 text-pink-800 border-pink-200' },
  travel_emergency: { label: 'Travel Emergency', icon: <Plane size={20} />, color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  pet_emergency: { label: 'Pet Emergency', icon: <Heart size={20} />, color: 'bg-green-100 text-green-800 border-green-200' }
};

export function EmergencyPlans() {
  const { user } = useAuth();
  const [plans, setPlans] = useState<HazardPlan[]>([]);
  const [profile, setProfile] = useState<EmergencyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hazardFilter, setHazardFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<HazardPlan | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<HazardPlan | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    loadProfileAndPlans();
  }, [user]);

  const loadProfileAndPlans = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get or create active profile
      let activeProfile = await EmergencyService.getActiveProfile(user.id);
      
      if (!activeProfile) {
        // Create a default personal profile
        activeProfile = await EmergencyService.createProfile({
          user_id: user.id,
          type: 'personal',
          status: 'active',
          locale: 'en-AU',
          name: 'My Emergency Plan',
          description: 'Personal emergency preparedness plan'
        });
        await EmergencyService.activateProfile(activeProfile.id);
      }
      
      setProfile(activeProfile);
      
      // Load plans
      const plansData = await EmergencyService.getHazardPlans(activeProfile.id);
      setPlans(plansData);
    } catch (err) {
      console.error('Error loading plans:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load emergency plans';
      setError(errorMessage);
      
      if (retryCount < 3) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          loadProfileAndPlans();
        }, 2000 * (retryCount + 1));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async (plan: HazardPlan) => {
    if (!profile) return;
    
    try {
      const newPlan = await EmergencyService.createHazardPlan({
        ...plan,
        profile_id: profile.id
      });
      setPlans(prev => [...prev, newPlan]);
      setShowAddModal(false);
    } catch (err) {
      console.error('Error creating plan:', err);
      setError(err instanceof Error ? err.message : 'Failed to create plan');
    }
  };

  const handleUpdatePlan = async (plan: HazardPlan) => {
    try {
      const updatedPlan = await EmergencyService.updateHazardPlan(plan.id, plan);
      setPlans(prev => prev.map(p => p.id === updatedPlan.id ? updatedPlan : p));
      setEditingPlan(null);
    } catch (err) {
      console.error('Error updating plan:', err);
      setError(err instanceof Error ? err.message : 'Failed to update plan');
    }
  };

  const handleDeletePlan = async (planId: string) => {
    if (!confirm('Are you sure you want to delete this emergency plan? This action cannot be undone.')) {
      return;
    }

    try {
      await EmergencyService.deleteHazardPlan(planId);
      setPlans(prev => prev.filter(p => p.id !== planId));
    } catch (err) {
      console.error('Error deleting plan:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete plan');
    }
  };

  const filteredPlans = plans.filter(plan => {
    const matchesSearch = hazardTypes[plan.hazard_type]?.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (plan.notes && plan.notes.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesHazard = hazardFilter === 'all' || plan.hazard_type === hazardFilter;
    
    return matchesSearch && matchesHazard;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
              <div className="h-12 bg-gray-200 rounded-lg w-32"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-80 bg-gray-200 rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="min-h-screen bg-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <AlertTriangle className="h-12 w-12 text-red-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Plans</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">{error}</p>
            <Button
              onClick={loadProfileAndPlans}
              variant="primary"
              className="flex items-center space-x-2"
            >
              <RefreshCw size={16} />
              <span>Try Again</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">My Emergency Plans</h1>
            <p className="text-xl text-gray-600">Create and manage your emergency preparedness plans</p>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            variant="primary"
            className="flex items-center space-x-2 px-6 py-3 text-lg"
          >
            <Plus size={20} />
            <span>New Plan</span>
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex-1">
            <SearchInput
              placeholder="Search plans by type or notes..."
              value={searchQuery}
              onChange={setSearchQuery}
              icon={<Search size={18} />}
              className="text-lg"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter size={18} className="text-gray-500" />
            <select
              value={hazardFilter}
              onChange={(e) => setHazardFilter(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent text-lg"
            >
              <option value="all">All Types</option>
              {Object.entries(hazardTypes).map(([key, value]) => (
                <option key={key} value={key}>{value.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Plans Grid */}
        <AnimatePresence>
          {filteredPlans.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-white border-0 shadow-xl">
                <div className="text-center py-16">
                  <div className="mx-auto w-24 h-24 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mb-6">
                    <FileText className="h-12 w-12 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {searchQuery || hazardFilter !== 'all' ? 'No plans found' : 'No emergency plans yet'}
                  </h3>
                  <p className="text-gray-600 mb-8 max-w-md mx-auto">
                    {searchQuery || hazardFilter !== 'all' 
                      ? 'Try adjusting your search or filter criteria'
                      : 'Start preparing for emergencies by creating your first emergency plan'
                    }
                  </p>
                  <Button
                    onClick={() => setShowAddModal(true)}
                    variant="primary"
                    className="flex items-center space-x-2 px-8 py-3 text-lg"
                  >
                    <Plus size={20} />
                    <span>Create Your First Plan</span>
                  </Button>
                </div>
              </Card>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPlans.map((plan, index) => {
                const hazardInfo = hazardTypes[plan.hazard_type];
                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    whileHover={{ y: -5, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card 
                      className="h-full cursor-pointer transition-all duration-300 hover:shadow-xl border-0 bg-white overflow-hidden"
                      onClick={() => setSelectedPlan(plan)}
                    >
                      {/* Plan Header */}
                      <div className={`h-32 bg-gradient-to-br ${hazardInfo?.color.split(' ')[0].replace('bg-', 'from-')} ${hazardInfo?.color.split(' ')[0].replace('bg-', 'to-')} relative`}>
                        <div className="absolute inset-0 bg-black/10"></div>
                        <div className="absolute top-4 right-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${hazardInfo?.color}`}>
                            {hazardInfo?.icon}
                            <span className="ml-1">{hazardInfo?.label}</span>
                          </span>
                        </div>
                        <div className="absolute bottom-4 left-4 text-white">
                          <h3 className="text-xl font-bold mb-1">{hazardInfo?.label}</h3>
                        </div>
                      </div>

                      {/* Plan Details */}
                      <div className="p-6">
                        <div className="space-y-4">
                          {/* Signals */}
                          {plan.signals && plan.signals.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">Warning Signals:</p>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {plan.signals.slice(0, 2).map((signal, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <AlertTriangle size={14} className="mr-2 mt-0.5 text-amber-500 flex-shrink-0" />
                                    <span className="line-clamp-1">{signal}</span>
                                  </li>
                                ))}
                                {plan.signals.length > 2 && (
                                  <li className="text-gray-500">+{plan.signals.length - 2} more</li>
                                )}
                              </ul>
                            </div>
                          )}

                          {/* First 60 Minutes */}
                          {plan.first_60_minutes && plan.first_60_minutes.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">First 60 Minutes:</p>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {plan.first_60_minutes.slice(0, 2).map((action, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <Clock size={14} className="mr-2 mt-0.5 text-blue-500 flex-shrink-0" />
                                    <span className="line-clamp-1">{action}</span>
                                  </li>
                                ))}
                                {plan.first_60_minutes.length > 2 && (
                                  <li className="text-gray-500">+{plan.first_60_minutes.length - 2} more</li>
                                )}
                              </ul>
                            </div>
                          )}

                          {/* Last Reviewed */}
                          {plan.last_reviewed_at && (
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <Clock size={14} />
                              <span>Last reviewed: {format(new Date(plan.last_reviewed_at), 'MMM dd, yyyy')}</span>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <div className="flex space-x-2">
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingPlan(plan);
                                }}
                                variant="outline"
                                size="sm"
                                className="flex items-center space-x-1"
                              >
                                <Edit size={14} />
                                <span>Edit</span>
                              </Button>
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePlan(plan.id);
                                }}
                                variant="outline"
                                size="sm"
                                className="flex items-center space-x-1 text-red-600 border-red-200 hover:bg-red-50"
                              >
                                <Trash2 size={14} />
                                <span>Delete</span>
                              </Button>
                            </div>
                            <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center">
                              <Eye size={16} className="text-white" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </AnimatePresence>

        {/* Plan Detail Modal */}
        {selectedPlan && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedPlan(null)}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {hazardTypes[selectedPlan.hazard_type]?.label}
                  </h2>
                  <button onClick={() => setSelectedPlan(null)} className="text-gray-400 hover:text-gray-600">
                    <XCircle size={24} />
                  </button>
                </div>
                
                <div className="space-y-6">
                  {selectedPlan.signals && selectedPlan.signals.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Warning Signals</h3>
                      <ul className="space-y-2">
                        {selectedPlan.signals.map((signal, idx) => (
                          <li key={idx} className="flex items-start">
                            <AlertTriangle size={18} className="mr-2 mt-0.5 text-amber-500 flex-shrink-0" />
                            <span className="text-gray-700">{signal}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedPlan.first_60_minutes && selectedPlan.first_60_minutes.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">First 60 Minutes Actions</h3>
                      <ol className="space-y-2">
                        {selectedPlan.first_60_minutes.map((action, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="flex-shrink-0 w-6 h-6 bg-idecide-primary text-white rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                              {idx + 1}
                            </span>
                            <span className="text-gray-700">{action}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {selectedPlan.notes && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Notes</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedPlan.notes}</p>
                    </div>
                  )}

                  {selectedPlan.last_reviewed_at && (
                    <div className="text-sm text-gray-500">
                      Last reviewed: {format(new Date(selectedPlan.last_reviewed_at), 'MMMM dd, yyyy')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Plan Modal - Simplified for now */}
        {(showAddModal || editingPlan) && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowAddModal(false); setEditingPlan(null); }}>
            <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingPlan ? 'Edit Emergency Plan' : 'Create Emergency Plan'}
              </h2>
              <p className="text-gray-600 mb-4">
                Plan creation form will be implemented here. For now, you can manage plans through the database.
              </p>
              <div className="flex justify-end gap-4">
                <Button variant="outline" onClick={() => { setShowAddModal(false); setEditingPlan(null); }}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

